import "./ai-consent-shared.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Col, Hr, Row } from "@amzn/stencil-react-components/layout";
import { Link, LinkIconPosition } from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { H2, Label, Text } from "@amzn/stencil-react-components/text";
import IconExternalLinkSmall from "@amzn/stencil-react-icons/icons/icon-external-link-small";
import { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AiAutomationChip } from "@/components/AIAutomation/AiAutomationChip";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import {
  emitJourneyConsentAbandoned,
  emitJourneyConsentAccepted,
  emitJourneyConsentDeclined,
  emitJourneyConsentPermissionToggled,
  emitJourneyConsentShown,
} from "@/helpers/journey-consent-metrics";
import { useSubmitUserConsentV2 } from "@/hooks/useSubmitUserConsentV2";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

import {
  type AiConsent,
  AI_CONSENT_TERMS_VERSION,
  isConsentComplete,
  writeAiConsent,
} from "./ai-consent";
import { GLOBAL_EMPLOYEE_PRIVACY_NOTICE_URL } from "./constants";

/** The only category the api-model offers for this agreement. */
const CONSENT_CATEGORY = "AI_SCREENSHOT_AUTOMATION";

const NOTHING_ALLOWED: AiConsent = {
  allowsDeviceControl: false,
  allowsScreenshotStorage: false,
};

const DEVICE_CONTROL_CHECKBOX_ID = "journey-consent-device-control";
const SCREENSHOT_STORAGE_CHECKBOX_ID = "journey-consent-screenshot-storage";

interface JourneyConsentScreenProps {
  /** Called once the answer is on record, so the host stops rendering this screen. */
  readonly onAnswered: () => void;
}

/**
 * Asks, once at the start of the journey, whether the assistant may act on this device and
 * whether screenshots may be stored for an IT Support Engineer to review.
 *
 * Capture does not begin here: identity verification is first and is never captured.
 */
export function JourneyConsentScreen({
  onAnswered,
}: JourneyConsentScreenProps): JSX.Element {
  const [permissions, setPermissions] = useState<AiConsent>(NOTHING_ALLOWED);
  /* Counts blocked attempts so the message remounts, which is what re-announces it. */
  const [blockedAttempts, setBlockedAttempts] = useState(0);
  const showConsentRequiredError = blockedAttempts > 0;
  const { mutate, isLoading, isError, reset } = useSubmitUserConsentV2();

  const { dispatch } = useContext(AppContext);
  const intl = useIntl();

  useEffect(() => {
    emitJourneyConsentShown();
    /*
     * This screen stands in for the whole wizard, so support has to be told the hire is on it.
     * Left alone, the handoff still reports step 1 and reads as a verification problem.
     */
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-ai-consent",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePermission = (
    permission: keyof AiConsent,
    isChecked: boolean
  ): void => {
    const updated = { ...permissions, [permission]: isChecked };
    setPermissions(updated);
    if (isConsentComplete(updated)) {
      setBlockedAttempts(0);
    }
    emitJourneyConsentPermissionToggled(permission, isChecked);
  };

  /*
   * Not waited on: a lost refusal costs nothing, and capture stays off either way. Every
   * grant-less exit comes through here, so a refusal is counted wherever it was made.
   */
  const recordRefusal = (wasAbandonedAfterFailure = false): void => {
    if (wasAbandonedAfterFailure) {
      emitJourneyConsentAbandoned();
    } else {
      emitJourneyConsentDeclined();
    }
    writeAiConsent(NOTHING_ALLOWED);
    onAnswered();
    mutate({
      consentCategory: CONSENT_CATEGORY,
      consentGranted: false,
      termsVersion: AI_CONSENT_TERMS_VERSION,
    });
  };

  // Recorded locally only in onSuccess: there is no endpoint to read consent back, so capture
  // must never run on a write that was lost.
  const recordGrant = (): void => {
    mutate(
      {
        consentCategory: CONSENT_CATEGORY,
        consentGranted: true,
        termsVersion: AI_CONSENT_TERMS_VERSION,
      },
      {
        onSuccess: () => {
          writeAiConsent(permissions);
          onAnswered();
        },
      }
    );
  };

  const handleAcceptClick = (): void => {
    const isComplete = isConsentComplete(permissions);
    emitJourneyConsentAccepted(isComplete);
    if (!isComplete) {
      setBlockedAttempts((attempts) => attempts + 1);
      return;
    }
    recordGrant();
  };

  if (isLoading) {
    return (
      <Spinner
        dataTestId="journeyConsentSubmittingSpinner"
        showText
        size={SpinnerSize.Medium}
      />
    );
  }

  /*
   * This screen stands in for the whole wizard, so an error needs a way out or the hire cannot
   * start Day 1 at all. Hence carrying on without the assistant, alongside the retry.
   */
  if (isError) {
    return (
      <Col gridGap="S400" dataTestId="journeyConsentSubmitError">
        <div role="alert">
          <MessageBanner type={MessageBannerType.Error}>
            <FormattedMessage
              id="error-somethingWentWrong"
              values={{
                chatWithITSupportLink: (
                  <ChatWithITSupportFlyoutLink localeMessageId="text-chatWithITSupport" />
                ),
              }}
            />
          </MessageBanner>
        </div>
        <Row gridGap="S300">
          {/* No accept metric here: the accept that failed already emitted one, and the denominator
              is emitted once on mount, so a retry would count one grant twice. */}
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => {
              reset();
              recordGrant();
            }}
            dataTestId="journeyConsentRetryButton"
          >
            <FormattedMessage id="aiConsent-retry-button" />
          </Button>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => recordRefusal(true)}
            dataTestId="journeyConsentContinueWithoutAiButton"
          >
            <FormattedMessage id="aiConsent-continue-without-ai-button" />
          </Button>
        </Row>
      </Col>
    );
  }

  return (
    <Col gridGap="S400" dataTestId="journeyConsentScreen">
      <Card className="ai-intro-banner">
        <Col alignItems="flex-start" justifyContent="center" gridGap="S100">
          {/* TODO: Move inline color to stylesheet after UX review - https://taskei.amazon.dev/tasks/NHE-5673 */}
          <H2
            fontSize="T300"
            fontWeight="bold"
            style={{ color: "#232F3E" }}
            dataTestId="journeyConsentHeading"
          >
            <FormattedMessage id="aiConsent-hero-title" />
          </H2>
        </Col>
      </Card>

      <Text fontWeight="regular">
        <FormattedMessage id="aiConsent-description" />
      </Text>

      <Text fontWeight="regular">
        <ul className="ai-how-it-works-list">
          <li>
            <FormattedMessage id="aiConsent-no-adverse-consequences" />
          </li>
          <li>
            <FormattedMessage
              id="aiConsent-personal-data-collection"
              values={{
                /*
                 * The icon goes through Link's own icon prop, and display is set explicitly:
                 * Stencil links are block by default, which put the glyph and the closing full
                 * stop on their own lines mid-sentence.
                 */
                privacyNoticeLink: (chunks: React.ReactNode[]) => (
                  <Link
                    href={GLOBAL_EMPLOYEE_PRIVACY_NOTICE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline"
                    icon={<IconExternalLinkSmall aria-hidden={true} />}
                    iconPosition={LinkIconPosition.Trailing}
                    iconVerticalAlign="middle"
                    dataTestId="journeyConsentPrivacyNoticeLink"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </li>
        </ul>
      </Text>

      <Col gridGap="S300">
        <Text fontWeight="bold">
          <FormattedMessage id="aiConsent-how-it-works-title" />
        </Text>
        <Text fontWeight="regular">
          <ul className="ai-how-it-works-list">
            <li>
              <FormattedMessage id="aiConsent-how-it-works-screenshots" />
            </li>
            <li>
              {/* The chip shows the hire the label itself, so its wording is the point. */}
              <Row alignItems="center" gridGap="S200" flexWrap="wrap">
                <FormattedMessage id="aiConsent-how-it-works-label" />
                <AiAutomationChip testIdPrefix="journeyConsentAiAutomation" />
              </Row>
              <FormattedMessage
                id="aiConsent-how-it-works-takes-control"
                values={{
                  /* The button's own label, so the quoted name tracks it in every locale. */
                  startButtonLabel: intl.formatMessage({
                    id: "connectVpn-ai-disclosure-start-button",
                  }),
                }}
              />
            </li>
          </ul>
        </Text>
      </Col>

      <Hr color="neutral20" size="narrow" />

      <Col gridGap="S200">
        <Row gridGap="S300" alignItems="flex-start">
          <Checkbox
            id={DEVICE_CONTROL_CHECKBOX_ID}
            name={DEVICE_CONTROL_CHECKBOX_ID}
            checked={permissions.allowsDeviceControl}
            onChange={(event) =>
              updatePermission("allowsDeviceControl", event.target.checked)
            }
            error={showConsentRequiredError && !permissions.allowsDeviceControl}
            dataTestId="journeyConsentDeviceControlCheckbox"
          />
          <Label
            color="color.text.primary"
            htmlFor={DEVICE_CONTROL_CHECKBOX_ID}
          >
            <Text fontSize="T200" className="ai-consent-label-text">
              <FormattedMessage id="aiConsent-device-control-consent-text" />
            </Text>
          </Label>
        </Row>

        <Row gridGap="S300" alignItems="flex-start">
          <Checkbox
            id={SCREENSHOT_STORAGE_CHECKBOX_ID}
            name={SCREENSHOT_STORAGE_CHECKBOX_ID}
            checked={permissions.allowsScreenshotStorage}
            onChange={(event) =>
              updatePermission("allowsScreenshotStorage", event.target.checked)
            }
            error={
              showConsentRequiredError && !permissions.allowsScreenshotStorage
            }
            dataTestId="journeyConsentScreenshotStorageCheckbox"
          />
          <Label
            color="color.text.primary"
            htmlFor={SCREENSHOT_STORAGE_CHECKBOX_ID}
          >
            <Text fontSize="T200" className="ai-consent-label-text">
              <FormattedMessage id="aiConsent-screenshot-storage-consent-text" />
            </Text>
          </Label>
        </Row>
      </Col>

      {showConsentRequiredError && (
        /*
         * An alert role, not Stencil's `aria-live` prop: that leaves nothing in the DOM, so the
         * hire is never told why Accept appeared to do nothing.
         */
        <div role="alert" key={blockedAttempts}>
          <MessageBanner
            type={MessageBannerType.Error}
            dataTestId="journeyConsentRequiredError"
          >
            <FormattedMessage id="aiConsent-error-required" />
          </MessageBanner>
        </div>
      )}

      <Row gridGap="S400">
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleAcceptClick}
          dataTestId="journeyConsentAcceptButton"
        >
          <FormattedMessage id="aiConsent-accept-button" />
        </Button>
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={() => recordRefusal()}
          dataTestId="journeyConsentDeclineButton"
        >
          <FormattedMessage id="aiConsent-decline-button" />
        </Button>
      </Row>
    </Col>
  );
}
